/**
 * Papapattes — Navigation drag-and-drop + modale éducative "première fois".
 *
 * La fiche suit le doigt/la souris 1:1 pendant le glissement (pas de
 * damping ni de plafond de déplacement) tant qu'un produit existe dans
 * la direction glissée. S'il n'y en a pas (bord de collection), une
 * légère résistance ("rubber band") freine le mouvement pour signaler
 * qu'on est en bout de liste. Au relâchement, si le seuil est atteint,
 * la fiche glisse complètement hors de l'écran avant de naviguer vers
 * le produit suivant/précédent — ce qui donne la sensation de "révéler"
 * le produit suivant plutôt qu'un simple changement de page brutal.
 */

// Interrupteur : mettre à true pour réactiver la navigation par glisser-déposer
// (PC + mobile). Désactivée à la demande de Sid (08/10/2026) ; code conservé.
// Les flèches / le sélecteur de produit restent actifs (gérés par breadcrumbs.liquid).
const PP_SWIPE_NAV_ENABLED = false;

document.addEventListener('DOMContentLoaded', () => {
  if (!PP_SWIPE_NAV_ENABLED) return;
  const nav   = document.querySelector('[data-pp-product-nav]');
  const fiche = document.querySelector('[data-pp-swipe-target]');
  if (!nav || !fiche) return;

  const prevArrow = document.querySelector('[data-pp-arrow-prev]');
  const nextArrow = document.querySelector('[data-pp-arrow-next]');

  const THRESHOLD      = 60;
  const EDGE_RESISTANCE = 0.25;
  const AXIS_LOCK_PX   = 8;
  const TIP_KEY        = 'pp_swipe_tip_seen';

  // Sélecteur des zones qui ne doivent jamais déclencher le glisser-déposer :
  // éléments interactifs natifs, rôles ARIA interactifs, contenu éditable,
  // et tout le widget d'avis Judge.me (classes préfixées "jdgm-") ainsi que
  // le bloc de traduction d'avis maison (pp-avis-*).
  const NO_SWIPE_SELECTOR = 'button, a, input, select, textarea, label, summary, details, .pp-combo, .product-form__input, ' +
    '[role="button"], [role="tab"], [role="link"], [role="menuitem"], ' +
    '[contenteditable="true"], [onclick], [class*="jdgm-"], [id*="jdgm-"], ' +
    '.pp-avis-fr, .pp-avis-toggle, .pp-avis-original, [data-pp-no-swipe]';

  const noMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  let active = false, captured = false, startX = 0, startY = 0;
  let axis = null, currentDelta = 0, activePointerId = null, leaving = false;

  function getPrev() { return nav.dataset.prevUrl || null; }
  function getNext() { return nav.dataset.nextUrl || null; }

  // ── Styles dynamiques ──
  if (!document.getElementById('pp-shake-style')) {
    const s = document.createElement('style');
    s.id = 'pp-shake-style';
    s.textContent = `
      @keyframes pp-shake {
        0%   { transform: translateX(0) rotate(0); }
        10%  { transform: translateX(-22px) rotate(-1.5deg); }
        22%  { transform: translateX(22px) rotate(1.5deg); }
        34%  { transform: translateX(-18px) rotate(-1deg); }
        46%  { transform: translateX(18px) rotate(1deg); }
        58%  { transform: translateX(-10px); }
        68%  { transform: translateX(10px); }
        78%  { transform: translateX(-5px); }
        88%  { transform: translateX(5px); }
        100% { transform: translateX(0) rotate(0); }
      }
      .pp-nav-shaking {
        animation: pp-shake 0.7s cubic-bezier(0.36, 0.07, 0.19, 0.97) both !important;
        transform-origin: center center;
      }

      .pp-nav-dragging {
        will-change: transform;
      }

      .pp-swipe-modal-overlay {
        position: fixed !important;
        inset: 0 !important;
        background: rgba(20, 20, 20, 0.55) !important;
        backdrop-filter: blur(2px);
        -webkit-backdrop-filter: blur(2px);
        z-index: 9998 !important;
        display: flex !important;
        align-items: center;
        justify-content: center;
        padding: 20px;
        opacity: 0;
        animation: pp-modal-fade-in 0.25s ease forwards;
      }
      @keyframes pp-modal-fade-in { to { opacity: 1; } }

      .pp-swipe-modal {
        position: relative;
        background: #ffffff;
        border-radius: 20px;
        width: 100%;
        max-width: 340px;
        padding: 32px 24px 24px;
        text-align: center;
        box-shadow: 0 24px 60px rgba(0,0,0,0.35);
        transform: scale(0.92) translateY(12px);
        opacity: 0;
        animation: pp-modal-pop-in 0.32s cubic-bezier(0.22,1,0.36,1) 0.05s forwards;
        font-family: 'Montserrat', sans-serif;
      }
      @keyframes pp-modal-pop-in { to { transform: scale(1) translateY(0); opacity: 1; } }

      .pp-swipe-modal__close {
        position: absolute;
        top: 12px;
        right: 12px;
        width: 28px;
        height: 28px;
        border: none;
        background: #F4F1EC;
        border-radius: 50%;
        color: #6b6b6b;
        font-size: 15px;
        line-height: 1;
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 0;
      }
      .pp-swipe-modal__close:hover { background: #EDE5DC; color: #2D2D2D; }

      .pp-swipe-modal__arrows {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 12px;
        font-size: 30px;
        line-height: 1;
        margin-bottom: 14px;
      }
      .pp-swipe-modal__arrow {
        color: #384799;
        display: inline-block;
        animation: pp-arrow-pulse 1.2s ease-in-out infinite;
      }
      .pp-swipe-modal__arrow--left  { animation-direction: reverse; }
      .pp-swipe-modal__arrow--right { animation-delay: 0.15s; }
      @keyframes pp-arrow-pulse {
        0%, 100% { transform: translateX(0);   opacity: 0.55; }
        50%      { transform: translateX(6px); opacity: 1; }
      }

      .pp-swipe-modal__title {
        font-weight: 700;
        font-size: 18px;
        color: #2D2D2D;
        margin: 0 0 10px;
      }
      .pp-swipe-modal__text {
        font-size: 14px;
        line-height: 1.55;
        color: #5c5c5c;
        margin: 0 0 22px;
      }
      .pp-swipe-modal__text strong { color: #2D2D2D; }

      .pp-swipe-modal__btn {
        width: 100%;
        background: #C2410C;
        color: #fff;
        border: none;
        border-radius: 50px;
        padding: 13px 20px;
        font-size: 14px;
        font-weight: 600;
        letter-spacing: 0.02em;
        cursor: pointer;
        transition: background 0.18s ease, transform 0.1s ease;
        font-family: inherit;
      }
      .pp-swipe-modal__btn:hover  { background: #a85c38; }
      .pp-swipe-modal__btn:active { transform: scale(0.98); }
    `;
    document.head.appendChild(s);
  }

  // ── Vibration (attire l'oeil sur la fiche juste avant la modale) ──
  function shakeFiche() {
    if (noMotion) return;
    fiche.classList.remove('pp-nav-shaking');
    void fiche.offsetWidth; // reflow
    fiche.classList.add('pp-nav-shaking');
    fiche.addEventListener('animationend', () =>
      fiche.classList.remove('pp-nav-shaking'), { once: true });
  }

  // ── Modale explicative ──
  function buildModalText() {
    if (getPrev() && getNext()) {
      return 'Sur cette fiche, glissez le doigt vers la gauche ou la droite (ou cliquez-glissez à la souris) pour passer directement au <strong>produit suivant ou précédent</strong> de cette collection, sans revenir en arrière.';
    }
    if (getNext()) {
      return 'Sur cette fiche, glissez le doigt vers la gauche (ou cliquez-glissez à la souris) pour passer directement au <strong>produit suivant</strong> de cette collection.';
    }
    return 'Sur cette fiche, glissez le doigt vers la droite (ou cliquez-glissez à la souris) pour revenir directement au <strong>produit précédent</strong> de cette collection.';
  }

  let modalEl = null;
  let lastFocused = null;

  function closeModal() {
    if (!modalEl) return;
    localStorage.setItem(TIP_KEY, '1');
    const el = modalEl;
    modalEl = null;
    document.removeEventListener('keydown', onModalKeydown);
    el.style.transition = 'opacity 0.2s ease';
    el.style.opacity = '0';
    setTimeout(() => el.remove(), 200);
    if (lastFocused && typeof lastFocused.focus === 'function') {
      lastFocused.focus();
    }
  }

  function onModalKeydown(e) {
    if (e.key === 'Escape') closeModal();
  }

  function showModal() {
    if (modalEl || localStorage.getItem(TIP_KEY)) return;
    if (!getPrev() && !getNext()) return;

    lastFocused = document.activeElement;

    const overlay = document.createElement('div');
    overlay.className = 'pp-swipe-modal-overlay';
    overlay.setAttribute('role', 'dialog');
    overlay.setAttribute('aria-modal', 'true');
    overlay.setAttribute('aria-labelledby', 'pp-swipe-modal-title');

    overlay.innerHTML = `
      <div class="pp-swipe-modal">
        <button type="button" class="pp-swipe-modal__close" aria-label="Fermer">✕</button>
        <div class="pp-swipe-modal__arrows" aria-hidden="true">
          ${getPrev() ? '<span class="pp-swipe-modal__arrow pp-swipe-modal__arrow--left">←</span>' : ''}
          ${getNext() ? '<span class="pp-swipe-modal__arrow pp-swipe-modal__arrow--right">→</span>' : ''}
        </div>
        <p class="pp-swipe-modal__title" id="pp-swipe-modal-title">Naviguez entre les produits</p>
        <p class="pp-swipe-modal__text">${buildModalText()}</p>
        <button type="button" class="pp-swipe-modal__btn">Compris, fermer</button>
      </div>
    `;

    overlay.addEventListener('click', (e) => {
      if (e.target === overlay) closeModal();
    });
    overlay.querySelector('.pp-swipe-modal__close').addEventListener('click', closeModal);
    overlay.querySelector('.pp-swipe-modal__btn').addEventListener('click', closeModal);

    document.body.appendChild(overlay);
    modalEl = overlay;
    document.addEventListener('keydown', onModalKeydown);
    overlay.querySelector('.pp-swipe-modal__btn').focus();
  }

  // Affiche la modale une seule fois, peu après le chargement — jamais
  // pendant un geste réel, pour ne pas l'interrompre.
  if (!localStorage.getItem(TIP_KEY) && (getPrev() || getNext())) {
    setTimeout(() => {
      shakeFiche();
      setTimeout(showModal, 550);
    }, 700);
  }

  // ── Flèches ──
  function setArrow(dir) {
    const hi = dir === 'prev' ? prevArrow : nextArrow;
    const lo = dir === 'prev' ? nextArrow : prevArrow;
    if (hi && !hi.hidden) hi.classList.add('is-active');
    if (lo) lo.classList.remove('is-active');
  }
  function clearArrows() {
    if (prevArrow) prevArrow.classList.remove('is-active');
    if (nextArrow) nextArrow.classList.remove('is-active');
  }

  // ── Drag : suit le doigt 1:1, avec résistance uniquement en bout de liste ──
  function applyDrag(dx) {
    currentDelta = dx;

    let visualDx = dx;
    if (dx > 0 && !getPrev())      visualDx = dx * EDGE_RESISTANCE;
    else if (dx < 0 && !getNext()) visualDx = dx * EDGE_RESISTANCE;

    if (!noMotion) {
      fiche.style.transform = `translateX(${visualDx}px)`;
    }
    if (dx > 6 && getPrev())       setArrow('prev');
    else if (dx < -6 && getNext()) setArrow('next');
    else clearArrows();
  }

  function arm(pointerId) {
    if (modalEl) return false;
    if (!getPrev() && !getNext()) return false;
    active = true; axis = null; currentDelta = 0; activePointerId = pointerId;
    fiche.classList.add('pp-nav-armed');
    return true;
  }

  function resetFiche(animate) {
    active = false; captured = false; activePointerId = null;
    fiche.classList.remove('pp-nav-armed', 'pp-nav-dragging');
    clearArrows();
    fiche.style.transition = animate ? 'transform 0.3s cubic-bezier(0.22,1,0.36,1)' : 'none';
    fiche.style.transform  = '';
    if (animate) setTimeout(() => { fiche.style.transition = ''; }, 400);
    currentDelta = 0; axis = null;
  }

  // Une fois le seuil dépassé au relâchement : la fiche continue son
  // mouvement jusqu'à sortir complètement de l'écran, "révélant" le
  // produit suivant/précédent, puis on navigue vers lui.
  function slideOutAndNavigate(url, direction) {
    active = false; captured = false; activePointerId = null;
    leaving = true;
    fiche.classList.remove('pp-nav-armed');
    clearArrows();

    if (noMotion) {
      window.location.href = url;
      return;
    }

    const distance = direction * (fiche.offsetWidth + 80);
    fiche.style.transition = 'transform 0.26s cubic-bezier(0.4,0,1,1), opacity 0.26s ease';
    fiche.style.transform  = `translateX(${distance}px)`;
    fiche.style.opacity    = '0.2';
    setTimeout(() => { window.location.href = url; }, 240);
  }

  function release() {
    if (!active) return;
    if (axis === 'h' && Math.abs(currentDelta) >= THRESHOLD) {
      const goingPrev = currentDelta > 0;
      const url = goingPrev ? getPrev() : getNext();
      if (url) {
        slideOutAndNavigate(url, goingPrev ? 1 : -1);
        return;
      }
    }
    resetFiche(true);
  }

  function cancel() { resetFiche(true); }

  function move(e) {
    if (!active || leaving) return;
    const dx = e.clientX - startX;
    const dy = e.clientY - startY;
    if (axis === null) {
      if (Math.abs(dx) < AXIS_LOCK_PX && Math.abs(dy) < AXIS_LOCK_PX) return;
      axis = Math.abs(dx) >= Math.abs(dy) ? 'h' : 'v';
    }
    if (axis === 'v') { cancel(); return; }
    // Dès que le glissement est confirmé horizontal, on empêche la galerie
    // photo native (qui a son propre gestionnaire de swipe) de récupérer
    // le même geste — sinon elle "mange" l'événement et rien ne bouge.
    e.stopPropagation();
    if (!captured) {
      try { fiche.setPointerCapture(activePointerId); captured = true; } catch(err) {}
    }
    fiche.classList.add('pp-nav-dragging');
    fiche.style.transition = 'none';
    applyDrag(dx);
  }

  // ── Listeners ──
  // En phase de capture (capture: true) : notre gestionnaire s'exécute
  // AVANT celui de la galerie photo native (élément descendant), qui lui
  // écoute en phase de bulle par défaut. Sans ça, un glissement démarré
  // directement sur l'image ne remonte parfois jamais jusqu'à la fiche.
  fiche.addEventListener('pointerdown', (e) => {
    if (leaving) return;
    if (e.button !== 0 && e.pointerType === 'mouse') return;
    if (e.target.closest(NO_SWIPE_SELECTOR)) return;
    startX = e.clientX; startY = e.clientY;
    arm(e.pointerId);
  }, { capture: true });
  fiche.addEventListener('pointermove', (e) => {
    if (!active || e.pointerId !== activePointerId) return;
    move(e);
  }, { capture: true });
  fiche.addEventListener('pointerup', (e) => {
    if (e.pointerId !== activePointerId) return;
    if (axis === 'h') e.stopPropagation();
    release();
  }, { capture: true });
  fiche.addEventListener('pointercancel', (e) => {
    if (e.pointerId !== activePointerId) return;
    if (axis === 'h') e.stopPropagation();
    cancel();
  }, { capture: true });
  fiche.addEventListener('dragstart', (e) => { if (active && axis === 'h') e.preventDefault(); }, { capture: true });
});
